import React from 'react'
import { View, StyleSheet } from 'react-native'
import BottomNav from './BottomNav'
import MerchantBottomNav from './MerchantBottomNav'
import MarketBottomNav from './MarketBottomNav'
import { useNavShell } from '../contexts/NavShellContext'

// Route → nav type mapping
const BUYER_NAV_ROUTES = new Set([
  'BuyerHome', 'SearchScreen', 'BuyerProfile', 'Discoveries',
  'Favorites', 'MapBrowse', 'StoreBrowse', 'BoothView',
  'Home', 'DraftProducts', 'MyStores', 'ProfileSettings', 'PurchaseHistory',
  'JoinMarket', 'Login', 'ProductDetail', 'SubmitOffer',
])

const MERCHANT_NAV_ROUTES = new Set([
  'MerchantDashboard', 'MerchantPOS', 'MerchantActivity', 'MerchantMore',
  'MerchantStaff', 'MerchantProductEdit', 'StoreInventory', 'StoreSettings',
  'StoreCamera', 'StoreCameraScreen', 'CategoryManagement', 'StockAdjustment',
  'PurchaseOrders', 'PurchaseOrderDetail', 'InventoryReports', 'SupplierManagement',
  'SocialAccounts',
])

const MARKET_NAV_ROUTES = new Set([
  'MarketAdmin', 'MarketSellers', 'MarketSpaces', 'MarketSettings',
  'MarketPayouts', 'MarketPOS', 'MarketPrintTags', 'MarketRefunds',
  'FloorPlanEditor', 'SpaceReservations',
])

// Routes that should never show a nav (leaf/detail screens with own back controls)
const NO_NAV_ROUTES = new Set([
  'Landing', 'Camera', 'StoreCamera', 'MerchantStoreSelect',
  'CreateStore', 'CreateMarket', 'Checkout', 'BulkUpload',
  'ReservationSettings', 'PrintTags', 'StorePrintTags', 'SpaceBrowse', 'VisualSpaceBrowse',
  'MyOffers', 'IncomingOffers', 'StoreTransactions',
  'ProductView', // reached from both buyer and merchant contexts — has own back button
])

type NavType = 'buyer' | 'merchant' | 'market' | 'none'

function getNavType(routeName: string): NavType {
  if (NO_NAV_ROUTES.has(routeName)) return 'none'
  if (MERCHANT_NAV_ROUTES.has(routeName)) return 'merchant'
  if (MARKET_NAV_ROUTES.has(routeName)) return 'market'
  if (BUYER_NAV_ROUTES.has(routeName)) return 'buyer'
  return 'none'
}

interface Props {
  children: React.ReactNode
}

export default function NavigationShell({ children }: Props) {
  const { hidden, routeName, routeParams } = useNavShell()

  const navType = hidden ? 'none' : getNavType(routeName)
  const storeId = routeParams?.storeId as string | undefined
  const marketId = routeParams?.marketId as string | undefined

  const renderNav = () => {
    switch (navType) {
      case 'buyer':
        return <BottomNav />
      case 'merchant':
        if (!storeId) return null
        return <MerchantBottomNav storeId={storeId} />
      case 'market':
        if (!marketId) return null
        return <MarketBottomNav marketId={marketId} />
      default:
        return null
    }
  }

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {children}
      </View>
      {renderNav()}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
})
